'use client';
import { Search, User, Users, X } from 'lucide-react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useChatMembers } from '@/hooks/queries/useChatMembers';

const getResolvedName = (member) => {
  const first = member.firstName?.trim();
  const last = member.lastName?.trim();
  if (first && last) return `${first} ${last}`;
  if (first) return first;
  if (last) return last;
  if (member.email) return member.email.split('@')[0];
  return 'Anonymous User';
};

export const NewConversationModal = ({
  isOpen,
  onClose,
  onSelectMember,
  dmMode: dmModeProp,
  isCurrentUserAdmin = false,
}) => {
  const dmMode = dmModeProp || (typeof window !== 'undefined' ? localStorage.getItem('dmMode') : null) || 'all';
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('admin');
  const [membersPage, setMembersPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: chatMembersData, isLoading } = useChatMembers({
    search: debouncedSearch || undefined,
    page: membersPage,
    pageSize: 20,
    enabled: isOpen,
  });

  const adminMembers = (chatMembersData?.admins ?? []).filter((m) => m.isDmEnabled !== false);
  const fanMembers = (chatMembersData?.members?.data ?? []).filter((m) => m.isDmEnabled !== false);
  const membersPagination = chatMembersData?.members?.pagination ?? { total: 0, page: 1, pageSize: 20 };
  const totalMembersPages = Math.ceil(membersPagination.total / membersPagination.pageSize) || 1;

  const showMembersTab = dmMode === 'all' || isCurrentUserAdmin;

  const handleSelect = (member) => {
    onSelectMember(member);
    onClose();
  };

  const handleReset = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setActiveTab('admin');
    setMembersPage(1);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setDebouncedSearch('');
      setActiveTab('admin');
      setMembersPage(1);
    }
  }, [isOpen]);

  if (!isOpen || dmMode === 'disabled' || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-9999 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-xs" onClick={handleReset} />
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full mx-4 h-[85vh] flex flex-col overflow-hidden ring-1 ring-black/6"
        style={{ maxWidth: '760px' }}
      >
        <div className="px-7 pt-6 pb-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 tracking-tight">New Message</h2>
              <p className="text-[13px] text-gray-400 mt-0.5">Start a conversation</p>
            </div>
            <button
              onClick={handleReset}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-[160px] shrink-0 border-r border-gray-100 py-3 px-2 flex flex-col gap-1">
            {[
              {
                key: 'admin',
                label: 'Admin Team',
                icon: (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
                    />
                  </svg>
                ),
              },
              ...(showMembersTab ? [{ key: 'members', label: 'Members', icon: <Users className="h-4 w-4" /> }] : []),
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  setMembersPage(1);
                }}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-medium transition-all text-left ${
                  activeTab === tab.key
                    ? 'bg-gray-900 text-white shadow-xs'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            <div className="px-5 py-3">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setMembersPage(1);
                  }}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-2 focus:ring-black/5 focus:border-gray-300 transition-all"
                />
              </div>
            </div>

            {!isLoading && (
              <div className="px-5 py-3 flex items-center justify-between border-t border-b border-gray-100 bg-gray-50/50">
                {activeTab === 'admin' && (
                  <p className="text-xs font-semibold text-gray-500">Admin Team ({adminMembers.length})</p>
                )}
                {activeTab === 'members' && (
                  <>
                    <p className="text-xs font-semibold text-gray-500">Members ({membersPagination.total})</p>
                    {totalMembersPages > 1 && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setMembersPage((p) => Math.max(1, p - 1))}
                          disabled={membersPage <= 1}
                          className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-xs"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <span className="text-sm font-medium text-gray-600 min-w-[60px] text-center">
                          Page {membersPage} of {totalMembersPages}
                        </span>
                        <button
                          onClick={() => setMembersPage((p) => Math.min(totalMembersPages, p + 1))}
                          disabled={membersPage >= totalMembersPages}
                          className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-xs"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-3 pb-2">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-5 h-5 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  {activeTab === 'admin' && (
                    <>
                      {adminMembers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                          <p className="text-sm font-medium text-gray-400">No admin team members found</p>
                        </div>
                      ) : (
                        <div className="py-1">
                          {adminMembers.map((member) => (
                            <button
                              key={member.forumUserUuid || member.uuid}
                              onClick={() => handleSelect(member)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50/60 transition-all text-left"
                            >
                              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 ring-1 ring-black/6">
                                {member.picture ? (
                                  <img src={member.picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-indigo-50 to-indigo-100">
                                    <User className="h-4 w-4 text-indigo-400" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-[13px] font-medium text-gray-700 truncate">
                                    {getResolvedName(member)}
                                  </p>
                                  <span className="shrink-0 rounded-full border border-black/10 bg-linear-to-b from-black/70 to-black p-px">
                                    <span className="flex px-1.5 py-0.5 rounded-full text-[9px] font-semibold text-white/90 bg-linear-to-b from-neutral-800 to-black">
                                      Admin
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === 'members' && (
                    <>
                      {fanMembers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                          <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            <Users className="h-5 w-5 text-gray-400" />
                          </div>
                          <p className="text-sm font-medium text-gray-400">No members found</p>
                          <p className="text-xs text-gray-300 mt-0.5">Try a different search term</p>
                        </div>
                      ) : (
                        <div className="py-1">
                          {fanMembers.map((member) => (
                            <button
                              key={member.forumUserUuid || member.uuid}
                              onClick={() => handleSelect(member)}
                              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50/60 transition-all text-left"
                            >
                              <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 ring-1 ring-black/6">
                                {member.picture ? (
                                  <img src={member.picture} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-gray-100 to-gray-200">
                                    <User className="h-4 w-4 text-gray-400" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-[13px] font-medium text-gray-700 truncate">
                                    {getResolvedName(member)}
                                  </p>
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-gray-100 text-gray-500 shrink-0">
                                    Member
                                  </span>
                                </div>
                                {(member.jobTitle || member.company) && (
                                  <p className="text-xs text-gray-400 truncate mt-0.5">
                                    {[member.jobTitle, member.company].filter(Boolean).join(' · ')}
                                  </p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

NewConversationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectMember: PropTypes.func.isRequired,
  dmMode: PropTypes.string,
  isCurrentUserAdmin: PropTypes.bool,
};
